import { UTM_PARAM_IDS } from '$lib/global/helper/get-query-parameter';
import { getAllTrackingProperties, getDefaultTrackingProperties } from '$lib/helper';

import type { EmbedContent } from '$lib/types/Components/Embed/EmbedContent';
import type { TrackingProperty } from '$lib/types/TrackingProperty';

// AI embeds default to non-sensitive (no overlay); users who do embed third-party
// tools via the AI block can opt in by setting isGdprSensitive: true. All other
// providers default to sensitive for backward compatibility.
export const resolveIsGdprSensitive = (content: EmbedContent): boolean =>
    content.isGdprSensitive ?? content.provider !== 'ai';

export const hasExternalContentOverlay = ({
    content,
    cookiesEnabled,
    hasAcceptedMediaOverlay,
}: {
    content: EmbedContent;
    cookiesEnabled: boolean;
    hasAcceptedMediaOverlay: boolean;
}): boolean => {
    const isGdprSensitive = resolveIsGdprSensitive(content);

    // Custom HTML and AI embeds that are not GDPR sensitive do not require an overlay
    if ((content.provider === 'custom-html' || content.provider === 'ai') && !isGdprSensitive) {
        return false;
    }

    // For all other embeds if cookies are not accepted yet, we show the overlay
    return !!(
        cookiesEnabled &&
        !hasAcceptedMediaOverlay &&
        (content.head || content.body || content.src)
    );
};

// Mapping of common short field names to their address-prefixed equivalents.
// When a fullAddress input is used, address components are stored with the
// "address." prefix (e.g., address.postalCode). This allows users to use
// short names like {{zip}} in embed blocks, falling back to the prefixed value.
const ADDRESS_FIELD_ALIASES: Record<string, string> = {
    zip: 'address.postalCode',
    postal: 'address.postalCode',
    postalCode: 'address.postalCode',
    city: 'address.city',
    street: 'address.street',
    country: 'address.country',
};

export const injectHtml = (html: string, targetNode: HTMLElement) => {
    const helper = document.createElement('div');
    helper.innerHTML = html;

    // We need to replace all script nodes in order for the browser to execute them.
    // script tags being placed in the DOM by setting innerHTML won't be executed.
    // script tags that are created with createElement and that are attached with
    // appendChild on the other hand are executed by the browser.
    const originalScriptNodes = helper.querySelectorAll('script');

    for (const originalScriptNode of originalScriptNodes) {
        const replacementScriptNode = document.createElement('script');

        for (const attribute of originalScriptNode.attributes) {
            const attributeValue = attribute.value;

            if (attributeValue && attributeValue.length > 0) {
                replacementScriptNode.setAttribute(attribute.name, attributeValue);
            }
        }

        replacementScriptNode.text = originalScriptNode.innerHTML;

        originalScriptNode.parentNode?.replaceChild(replacementScriptNode, originalScriptNode);
    }

    targetNode.append(...Array.from(helper.childNodes));
};

export const createUniqueCrossOriginIframeSandboxUrl = (uniqueIdentifier: string): URL => {
    return new URL(`https://${uniqueIdentifier}-funnel-app.perspective-sandbox.co/funnel-app.html`);
};

// FUN-3908: Sandboxed embeds start at height 0 and only grow once the
// cross-origin sandbox has loaded and measured its content. Blocks below the
// embed (e.g. a CTA button) would flash at the top of the page and then jump
// down. To avoid this layout shift on repeat visits, we cache the last
// measured height per block and viewport width for the duration of the session.
const EMBED_HEIGHT_CACHE_PREFIX = 'perspective.embed-height.';

// CVT-4096: Any height the sandbox measured for its injected content is valid,
// including a very small one — an AI divider settles at ~8px. Heights are only
// read/written once the sandbox has confirmed its content is in the DOM, so
// there is no need to second-guess the value itself.
export const isValidEmbedHeight = (height: number): boolean =>
    Number.isFinite(height) && height >= 0;

export const getEmbedHeightCacheKey = (
    campaignId: string,
    blockId: string,
    viewportWidth: number,
): string => `${EMBED_HEIGHT_CACHE_PREFIX}${campaignId}.${blockId}.${viewportWidth}`;

export const readCachedEmbedHeight = (cacheKey: string): number | null => {
    try {
        const rawValue = window.sessionStorage.getItem(cacheKey);
        const height = rawValue === null ? NaN : parseFloat(rawValue);

        return isValidEmbedHeight(height) ? height : null;
    } catch {
        return null;
    }
};

export const writeCachedEmbedHeight = (cacheKey: string, height: number): void => {
    if (!isValidEmbedHeight(height)) {
        return;
    }

    try {
        window.sessionStorage.setItem(cacheKey, String(height));
    } catch {
        // Ignore storage failures (e.g. privacy mode) — worst case we fall
        // back to the loading placeholder on the next visit.
    }
};

/**
 * `{{ variableName }}` placeholder. The name is captured loosely (anything but braces) and
 * trimmed, so it is matched literally rather than being spliced into a regex — a name like
 * `address.postalCode` would otherwise have its `.` act as a wildcard.
 */
const PLACEHOLDER_PATTERN = /\{\{([^{}]+)\}\}/g;

/**
 * Characters that cannot appear inside a URL in embedded HTML or JS, and therefore mark the
 * start of the URL when scanning backwards from a placeholder.
 */
const URL_BOUNDARY_CHARS = new Set([' ', '\t', '\n', '\r', '"', "'", '`', '<', '>', '(', ')']);

/**
 * Whether a placeholder sits in the value position of a URL query parameter, i.e. somewhere
 * after a `?`/`&`/`#` and after the `=` of a `key=` pair, without leaving the URL token.
 *
 * Scanning the surrounding template is deliberately lexical: an embed block holds arbitrary
 * HTML and JS, and the same variable is routinely used in several contexts at once (a query
 * parameter in an `href`, and a JS string literal handed to a widget's prefill API). Only the
 * URL case may be percent-encoded, so the position has to be decided per occurrence.
 *
 * Note this also handles `&amp;` separators (HTML-escaped embed markup) and back-to-back
 * placeholders such as `?name={{firstName}}%20{{lastName}}`, because neither `;` nor `{`/`}`
 * is a boundary character.
 */
const isQueryParameterValuePosition = (template: string, placeholderStart: number): boolean => {
    let sawAssignment = false;

    for (let index = placeholderStart - 1; index >= 0; index -= 1) {
        const char = template[index];

        if (URL_BOUNDARY_CHARS.has(char)) {
            return false;
        }

        if (char === '=') {
            sawAssignment = true;
            continue;
        }

        if (char === '?' || char === '&' || char === '#') {
            return sawAssignment;
        }
    }

    return false;
};

export const interpolateHeadAndBody = (
    trackingProperties: TrackingProperty[],
    globalVariables: Record<string, string>,
    campaignId: string,
    scripts: { head: string; body: string },
): { head: string; body: string } => {
    const defaultTrackingProperties = getDefaultTrackingProperties(trackingProperties);
    const savedTrackingProperties = getAllTrackingProperties(campaignId);
    const values = {
        ...defaultTrackingProperties,
        ...savedTrackingProperties,
        ...globalVariables,
    };

    const allVariableNames: Set<string> = new Set([
        // we interpolate all supported UTM parameters,
        // if some parameter is not defined, it will be replaced
        // by the fallback '' (empty string)
        ...UTM_PARAM_IDS,
        ...Object.keys(defaultTrackingProperties),
        ...Object.keys(savedTrackingProperties),
        ...Object.keys(globalVariables),
    ]);

    // Resolve address field aliases: if a short name (e.g., "zip") has no value,
    // fall back to the address-prefixed equivalent (e.g., "address.postalCode")
    for (const [shortName, addressFieldName] of Object.entries(ADDRESS_FIELD_ALIASES)) {
        if (!values[shortName] && values[addressFieldName]) {
            values[shortName] = values[addressFieldName];
            allVariableNames.add(shortName);
        }
    }

    // FUN-4081: A single pass over the template, so that a value which happens to contain
    // `{{…}}` is not interpolated again by a later variable, and so that each occurrence can be
    // encoded according to where it sits. A replacer function is used so `$&` and friends inside
    // an answer are inserted verbatim instead of being read as replacement patterns.
    const interpolate = (template: string) =>
        template.replace(PLACEHOLDER_PATTERN, (match, rawName: string, offset: number) => {
            const variableName = rawName.trim();

            // Unknown placeholders are left untouched, and the Set membership check keeps
            // `{{toString}}` from resolving against Object.prototype.
            if (!allVariableNames.has(variableName)) {
                return match;
            }

            const variableValue = String(values[variableName] ?? '');

            // A literal `+` in a query parameter value is decoded as a space by the receiving
            // server (`application/x-www-form-urlencoded`), so `€500K+` arrived as `€500K `.
            // Percent-encoding also stops `&`, `=` and `#` in an answer from injecting or
            // truncating query parameters.
            return isQueryParameterValuePosition(template, offset)
                ? encodeURIComponent(variableValue)
                : variableValue;
        });

    return {
        head: interpolate(scripts.head ?? ''),
        body: interpolate(scripts.body ?? ''),
    };
};
